import { styled } from "@linaria/react";
import * as React from "react";
import { useModals } from "@ui/lib/hooks/useModal";
import t from "../../../../../i18n";
import Modal from "@ui/Modal";
import ImportModelModalForm from "./BatchModelImport/ImportModelModalForm";
import { DeleteInputBindingModal, ImportInputBinding, QuantityInputInfo } from "./InputBinding";
import {
  DeleteModelImageModal,
  DeleteModelModal,
  ImportModelImage,
  ImportModelModal,
  ModelInfo,
} from "./Model";
import { DeleteOutputBindingModal, ImportOutputBinding, QuantityOutputInfo } from "./OutputBinding";
import { useProject } from "../../../../../api/useProjects";
import {
  SimulationStatus,
  IncompleteSimulationCauses,
  getSimulationSetupErrorLogs,
  getSimulationModelSetupOutputErrorLogs,
  useSetup,
} from "../../../../../api/useSetups";
import { useCanUserDoOnProject } from "../../../../canUserDo";
import { StatusDisplay, mapStatusDisplay } from "../StatusDisplay/utils";
import Banner from "../StatusDisplay/Banner";
import BannerTextList from "../StatusDisplay/BannerTextList";
import BannerTitle from "../StatusDisplay/BannerTitle";
import ErrorLog from "../StatusDisplay/ErrorLog";
import BlankStateV2 from "@ui/BlankStateV2";
import { H3 } from "@ui/Text";
import { css } from "@linaria/core";
import { LoadedFileName } from "../../LoadedFileName";
import { useQuantityIndex } from "../../../../../api/useQuantities";
import { useParams } from "react-router";

const Container = styled.div`
  padding: 48px;
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: max-content;
  gap: 48px;
  /* display: flex; */
  /* flex-direction: column; */
  /* gap: 48px; */
  /* flex-wrap: wrap; */
`;

const SectionTitle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const simulationBannerStatus = mapStatusDisplay<SimulationStatus>([
  [SimulationStatus.INCOMPLETE, StatusDisplay.PENDING],
  [SimulationStatus.INVALID, StatusDisplay.ERROR],
  [SimulationStatus.VALID, StatusDisplay.SUCCESS],
  [SimulationStatus.VALIDATING, StatusDisplay.WARNING],
]);

function getSimulationStatusExplanation(status: SimulationStatus): string {
  switch (status) {
    case SimulationStatus.INCOMPLETE:
      return t("setup.simulation.status.incomplete");
    case SimulationStatus.INVALID:
      return t("setup.simulation.status.invalid");
    case SimulationStatus.VALID:
      return t("setup.simulation.status.valid");
    case SimulationStatus.VALIDATING:
      return t("setup.simulation.status.validating");
    default:
      throw new Error(status satisfies never);
  }
}

function getIncompleteSimulationCauseExplanations(causes: IncompleteSimulationCauses[]): string[] {
  return causes.map(cause => {
    switch (cause) {
      case IncompleteSimulationCauses.NO_MODEL:
        return t("setup.simulation.status.incomplete.noModel");
      case IncompleteSimulationCauses.NO_INPUT:
        return t("setup.simulation.status.incomplete.noInput");
      case IncompleteSimulationCauses.NO_OUTPUT:
        return t("setup.simulation.status.incomplete.noOutput");
      default:
        throw new Error(cause satisfies never);
    }
  });
}

export const Simulation = () => {
  const { projectId, setupId } = useParams();
  if (!projectId) throw new Error("no project");
  if (!setupId) throw new Error("no setup");

  const project = useProject(projectId);
  const setup = useSetup({ projectId, setupId });

  if (!project) throw new Error("no project");
  if (!setup) throw new Error("no setup");

  const simulationErrorLogs = getSimulationSetupErrorLogs(setup);
  const simulationModelOutputErrorLogs = getSimulationModelSetupOutputErrorLogs(setup);
  const canImportSetup = useCanUserDoOnProject("PROJECT_SETUP", project.id);
  const { modal, closeModal, openModal } = useModals<string>();
  const quantityIndex = useQuantityIndex(projectId);

  const hasSimulationSetup =
    setup.simulationModel !== null ||
    setup.simulationInputs.length > 0 ||
    setup.simulationOutputs.length > 0;

  const [
    simulationStatus = SimulationStatus.INVALID,
    incompleteSimulationCauses = [IncompleteSimulationCauses.NO_INPUT],
  ] = setup.simulationValidity;

  return (
    <>
      {hasSimulationSetup ? (
        <>
          <Banner status={simulationBannerStatus(simulationStatus)}>
            <BannerTitle withSpinner={simulationStatus === SimulationStatus.VALIDATING}>
              {getSimulationStatusExplanation(simulationStatus)}
            </BannerTitle>
            {simulationStatus === SimulationStatus.INCOMPLETE && (
              <BannerTextList
                explanations={getIncompleteSimulationCauseExplanations(incompleteSimulationCauses)}
              />
            )}
            {simulationStatus === SimulationStatus.INVALID &&
              (simulationErrorLogs || simulationModelOutputErrorLogs) && (
                <ErrorLog logs={(simulationModelOutputErrorLogs ?? simulationErrorLogs) || ""} />
              )}
          </Banner>

          <Container>
            <ModelInfo projectId={projectId} setup={setup} openModal={openModal} />
            <section>
              <div className={SectionTitle}>
                <H3> {t("setup.paragraphs.quantities.input")}</H3>
                <LoadedFileName fileName={setup.modelInputsFileName} />
              </div>
              <QuantityInputInfo
                setup={setup}
                quantityIndex={quantityIndex}
                projectId={projectId}
                openModal={openModal}
              />
            </section>
            <section>
              <div className={SectionTitle}>
                <H3>{t("setup.paragraphs.quantities.output")}</H3>
                <LoadedFileName fileName={setup.modelOutputsFileName} />
              </div>
              <QuantityOutputInfo
                setup={setup}
                quantityIndex={quantityIndex}
                project={project}
                openModal={openModal}
              />
            </section>
          </Container>
        </>
      ) : (
        <BlankStateV2
          title={t("setup.simulation.blank_state.title")}
          subtitle={canImportSetup ? t("setup.simulation.blank_state.text") : null}
          onClick={canImportSetup ? () => openModal("importBatch") : undefined}
          buttonLabel={canImportSetup ? t("setup.simulation.blank_state.button") : undefined}
        />
      )}
      <Modal
        title={t("setup.simulation.import_files")}
        isOpen={modal === "importBatch"}
        onRequestClose={closeModal}
      >
        <ImportModelModalForm project={project} setup={setup} onRequestClose={closeModal} />
      </Modal>
      <Modal
        title={t("setup.simulation.import_files")}
        isOpen={modal === "importModel"}
        onRequestClose={closeModal}
      >
        <ImportModelModal project={project} setup={setup} onRequestClose={closeModal} />
      </Modal>
      <Modal
        title={t("setup.simulation.import_files")}
        isOpen={modal === "replaceModel"}
        onRequestClose={closeModal}
      >
        <ImportModelModal
          project={project}
          setup={setup}
          onRequestClose={closeModal}
          displayWarning
        />
      </Modal>
      <Modal
        title={t("setup.paragraphs.model.modal.delete_model.title")}
        isOpen={modal === "deleteModel"}
        onRequestClose={closeModal}
      >
        <DeleteModelModal project={project} setup={setup} onRequestClose={closeModal} />
      </Modal>
      <Modal
        title={t("setup.paragraphs.model.modal.delete_image.title")}
        isOpen={modal === "deleteImage"}
        onRequestClose={closeModal}
      >
        <DeleteModelImageModal project={project} setup={setup} onRequestClose={closeModal} />
      </Modal>
      <Modal
        title={t("setup.paragraphs.model.modal.update_image.title")}
        isOpen={modal === "importImage"}
        onRequestClose={closeModal}
      >
        <ImportModelImage onRequestClose={closeModal} project={project} setup={setup} />
      </Modal>
      <Modal
        title={t("setup.paragraphs.quantities.input.import.button")}
        isOpen={modal === "importInput"}
        onRequestClose={closeModal}
      >
        <ImportInputBinding onRequestClose={closeModal} project={project} setup={setup} />
      </Modal>
      <Modal
        title={t("setup.paragraphs.quantities.input.import.button")}
        isOpen={modal === "replaceInput"}
        onRequestClose={closeModal}
      >
        <ImportInputBinding onRequestClose={closeModal} project={project} setup={setup} />
      </Modal>
      <Modal
        title={t("setup.paragraphs.quantities.output.import.button")}
        isOpen={modal === "importOutput"}
        onRequestClose={closeModal}
      >
        <ImportOutputBinding onRequestClose={closeModal} project={project} setup={setup} />
      </Modal>
      <Modal
        title={t("setup.paragraphs.quantities.output.import.button")}
        isOpen={modal === "replaceOutput"}
        onRequestClose={closeModal}
      >
        <ImportOutputBinding onRequestClose={closeModal} project={project} setup={setup} />
      </Modal>
      <Modal
        title={t("setup.paragraphs.model.modal.delete_input.title")}
        isOpen={modal === "deleteInput"}
        onRequestClose={closeModal}
      >
        <DeleteInputBindingModal onRequestClose={closeModal} project={project} setup={setup} />
      </Modal>
      <Modal
        title={t("setup.paragraphs.model.modal.delete_output.title")}
        isOpen={modal === "deleteOutput"}
        onRequestClose={closeModal}
      >
        <DeleteOutputBindingModal onRequestClose={closeModal} project={project} setup={setup} />
      </Modal>
    </>
  );
};
